<template>
  <div class="card">
    <div class="card-content">
      <div class="image-container">
        <div
          v-if="roomImages.length > 0"
          :key="visibleImage.url"
          class="image-wrapper"
        >
          <transition name="fade">
            <img
              :key="visibleImage.url"
              :src="visibleImage.url"
              :alt="visibleImage.description"
              class="room-image"
            />
          </transition>
          <div class="image-overlay">
            <div class="navigation-buttons">
              <button
                @click="prevImage(room.roomImages.indexOf(visibleImage))"
                class="nav-button left"
              >
                <i class="fas fa-circle-chevron-left"></i>
              </button>
              <button
                @click="nextImage(room.roomImages.indexOf(visibleImage))"
                class="nav-button right"
              >
                <i class="fas fa-circle-chevron-right"></i>
              </button>
            </div>
            <div class="image-counter">
              <i class="fas fa-images"></i>
              {{ room.roomImages.indexOf(visibleImage) + 1 }} of
              {{ room.roomImages.length }}
            </div>
          </div>
        </div>
      </div>

      <div class="room-info">
        <h2 class="room-title">
          <i class="fas fa-bed"></i>
          {{ roomDescription.bedMessage }} - {{ roomDescription.type }}
        </h2>

        <ul v-if="room.commonDescriptions" class="feature-list">
          <li v-for="(part, index) in room.commonDescriptions" :key="index">
            <i class="fas fa-check-circle"></i>{{ part }}
          </li>
        </ul>
      </div>

      <div class="cancellation-policy">
        <div class="refundable-title">Cancellation Policy:</div>
        <div class="rate-options">
          <label v-if="room.lowestNonRefRate" class="rate-option" :data-rate-key="room.lowestNonRefRate.key">
            <input
              type="radio"
              :name="componentUniqueName"
              :id="room.lowestNonRefRate.key"
              :value="room.lowestNonRefRate.key"
              v-model="selectedRateId"
              @click="onRateClick(room.lowestNonRefRate)"
            />
            <span class="rate-details">
              <span>Non-refundable
                <span v-if="room.lowestNonRefRate.isMemberRate" class="member-rate-tag">Member Only</span>
              </span>
              <span class="rate-price"
                >{{
                  room.lowestNonRefRate.requestedCurrencyPricing.dailyRate | formatCurrency(0, currencyCode)
                }}</span
              >
            </span>
          </label>
          <label v-else class="rate-option disabled">
            <input type="radio" disabled />
            <span class="rate-details has-text-grey-light">Non-refundable</span>
          </label>

          <label
            v-for="rate in room.lowestCancelableRates"
            :key="rate.key"
            class="rate-option"
            :data-rate-key="rate.key"
            :data-rph="rate.RPH"
          >
            <input
              type="radio"
              :name="componentUniqueName"
              :id="rate.key"
              :value="rate.key"
              v-model="selectedRateId"
              @click="onRateClick(rate)"
            />
            <span class="rate-details">
              <span
                >Free cancellation until
                {{
                  luxon
                    .fromISO(rate.cancelPenalties[0].deadline)
                    .toFormat("MMMM d")
                }}
                <span v-if="rate.isMemberRate" class="member-rate-tag">Member Only</span>
              </span
              >
              <span class="rate-price"
                >{{ getAverageBaseNightlyRate(rate) | formatCurrency(0, currencyCode) }} 
                <span class="is-size-7 has-text-grey">/ night</span>
              </span
              >
            </span>
          </label>
        </div>

        <template v-if="moreRates.length > 0">
          <div v-if="showMoreRates" class="additional-rates">
            <template v-if="selectedRate.nonrefundable === true">
              <div class="refundable-title" :class="nonrefundableRates.length === 0 ? 'disabled' : '' ">Non-refundable rates:</div>
              <label
                v-for="rate in nonrefundableRates"
                :key="rate.key"
                class="rate-option"
                :data-rate-key="rate.key"
                :data-rph="rate.RPH"
              >
                <input
                  type="radio"
                  :name="componentUniqueName"
                  :id="rate.key"
                  :value="rate.key"
                  v-model="selectedRateId"
                  @click="selectedRateId = rate.key"
                />
                <span class="rate-details">
                  <span>
                    {{
                      rate.descriptionItems[0]
                        ? rate.descriptionItems[0]
                        : "Standard rate"
                    }}
                    <span
                      v-if="rate.enhancedPricing.ratePlanCode !== '***'"
                      class="rate-code"
                      >{{ rate.enhancedPricing.ratePlanCode }}</span
                    >
                    <span v-if="rate.isMemberRate" class="member-rate-tag">Member Only</span>
                  </span>
                  <span class="rate-price"
                    >
                    {{ getAverageBaseNightlyRate(rate) | formatCurrency(0, currencyCode) }}
                    <span class="is-size-7 has-text-grey">/ night</span>
                  </span
                  >
                </span>
              </label>
            </template>
            <template v-if="selectedRate.nonrefundable === false">
              <div class="refundable-title" :class="refundableRates.length === 0 ? 'disabled' : '' ">Refundable rates:</div>
              <label
                v-for="rate in refundableRates"
                :key="rate.key"
                class="rate-option"
                :data-rate-key="rate.key"
                :data-rph="rate.RPH"
              >
                <input
                  type="radio"
                  :name="componentUniqueName"
                  :id="rate.key"
                  :value="rate.key"
                  v-model="selectedRateId"
                  @click="selectedRateId = rate.key"
                />
                <span class="rate-details">
                  <span>
                    {{
                      rate.descriptionItems[0]
                        ? rate.descriptionItems[0]
                        : "Standard rate"
                    }}
                    <span
                      v-if="rate.enhancedPricing.ratePlanCode !== '***'"
                      class="rate-code"
                      >{{ rate.enhancedPricing.ratePlanCode }}</span
                    >
                    <span v-if="rate.isMemberRate" class="member-rate-tag">Member Only</span>
                  </span>
                  <span class="rate-price"
                    >{{ getAverageBaseNightlyRate(rate) | formatCurrency(0, currencyCode) }}
                    <span class="is-size-7 has-text-grey">/ night</span>
                    </span
                  >
                </span>
              </label>
            </template>
            <button class="toggle-rates" @click="showMoreRates = false">
              Hide rates <i class="fas fa-angle-up"></i>
            </button>
          </div>

          <button v-else class="toggle-rates" @click="showMoreRates = true">
            More rates <i class="fas fa-angle-down"></i>
          </button>
        </template>
      </div>

      <div v-if="selectedRate" class="selected-rate-details">
        
        <!-- <div class="price-summary">
          <p class="nightly-rate">
           {{ getAverageBaseNightlyRate(selectedRate) | formatCurrency(0, currencyCode) }}
            <span class="rate-period"> / night</span>
          </p>
          <p class="total-price">
            {{ getTotalBaseNightlyRate(selectedRate) | formatCurrency(0, currencyCode) }}
            <span class="stay-duration"
              >subtotal for {{ selectedRate.nights }} nights</span
            >
          </p>
        </div> -->
 
        <div class="price-inline-container mb-5">
          <div>
            <span class="nightly-price">{{ getAverageBaseNightlyRate(selectedRate) | formatCurrency(0, currencyCode) }}</span>
            <span class="price-label ml-1">/ night</span>
          </div>
          <span class="price-separator">|</span>
          <div>
            <span class="price-label">
              Subtotal: <span class="price-value">{{ getTotalBaseNightlyRate(selectedRate) | formatCurrency(0, currencyCode) }}</span>
            </span>
          </div>
          <span class="price-separator">|</span>
          <div>
            <span class="price-label">
              Total: <span class="total-value">{{ getTotal(selectedRate) | formatCurrency(0, currencyCode) }}</span>
            </span>
          </div>
          
          <!--   -->
          
          <!-- <span class="price-separator">|</span> -->
          
        </div>

        <div class="rate-tags">
          <span
            v-if="selectedRate.enhancedPricing.ratePlanCode !== '***'"
            class="rate-code"
            >{{ selectedRate.enhancedPricing.ratePlanCode }}</span
          >
          <span v-if="selectedRate.breakfastIncluded" class="amenity-tag">
            <i class="fas fa-mug-saucer"></i> Free Breakfast
          </span>
          <span v-if="selectedRate.freeWifi" class="amenity-tag">
            <i class="fas fa-wifi"></i> Free Wifi
          </span>
          <span v-if="isWebBedsHotel(selectedRate.infoSource)" class="amenity-tag">
            <i class="fa fa-ban mr-1"></i> No Loyalty Points
          </span>
        </div>

        <ul class="feature-list">
          <li
            v-for="(part, index) in selectedRate.descriptionItems"
            :key="index"
          >
            <i class="fas fa-check-circle"></i>{{ part }}
          </li>
        </ul>

        <div v-if="selectedRate.cancelPenalties" class="cancel-penalties mt-4 mb-4">
          <CancelPenalty
            v-for="(cancelPenalty, index) in selectedRate.cancelPenalties"
            :key="index"
            :cancelPenalty="cancelPenalty"
          ></CancelPenalty>
        </div>

        <p v-if="selectedRate.nonrefundable" class="non-refundable-warning">
          <i class="fas fa-exclamation-triangle"></i> Non-refundable
        </p>

        <div class="policy-tag mb-2">
          <OutOfPolicyTag
            v-if="policyViolationsCount > 0"
            :travelPolicy="selectedRate.travelPolicy"
          ></OutOfPolicyTag>
        </div>

        <div class="has-flex">
          <div
            @click="epTabOpen = !epTabOpen"
            style="cursor: pointer;font-size: 0.775rem;"
          >
            <span v-if="!epTabOpen"
              >View Rate / Cancellation Details
              <i class="fa-solid fa-angle-down"></i
            ></span>
            <span v-else
              >Hide Rate / Cancellation Details
              <i class="fa-solid fa-angle-up"></i
            ></span>
          </div>
        </div>
        <KeepAlive>
          <EnhancedPricingTab
            v-if="epTabOpen"
            :sol="selectedRate"
            :session="sessionKey"
          />
        </KeepAlive>

        <p
          v-if="
            selectedRate.isSelectable &&
              selectedRate.immediateTravelBookingAdvice == 'prevent'
          "
          class="booking-advice"
        >
          To book this option call: <br />
          877-924-0303
        </p>

        <button
          v-if="
            selectedRate.isSelectable &&
              selectedRate.immediateTravelBookingAdvice != 'prevent'
          "
          class="book-button"
          @click="submitted(selectedRate)"
        >
          Book Room
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import CancelPenalty from "./CancelPenalty.vue"
import OutOfPolicyTag from "../../Tags/OutOfPolicyTag.vue"
import { DateTime } from "luxon"
import { mapState } from "vuex"
import EnhancedPricingTab from "@/components/BookTravel/Hotel/EnhancedPricingTab"
import meetingsMixin from "@/mixins/meetingsMixin"
import { get } from "jquery"

export default {
  mixins: [meetingsMixin],
  components: {
    CancelPenalty,
    OutOfPolicyTag,
    EnhancedPricingTab,
  },
  props: {
    room: Object,
    hotel: Object,
    sessionKey: String,
  },
  data() {
    return {
      showMoreRates: false,
      selectedRateId: null,
      visibleImage: null,
      epTabOpen: false,
      selectedDeadline: null,
    }
  },
  computed: {
    ...mapState({
      booking: (state) => state.booking.hotel,
    }),
    luxon() {
      return DateTime
    },
    componentUniqueName() {
      return Math.random()
        .toString(36)
        .substring(2, 8)
        .toUpperCase()
    },
    highlightRates() {
      return this.room.lowestNonRefRate
        ? [...this.room.lowestCancelableRates, this.room.lowestNonRefRate]
        : this.room.lowestCancelableRates
    },
    moreRates() {
      const codes = this.highlightRates.map((rate) => rate.key)
      return this.room.rates.filter((rate) => !codes.includes(rate.key))
    },
    refundableRates() {
      if (!this.room.refundableRates) {
        return []
      }
      const codes = this.room.lowestCancelableRates.map((rate) => rate.key)
      let filtered = this.room.refundableRates.filter(
        (rate) => !codes.includes(rate.key)
      )

      if (this.selectedDeadline !== null) {
        filtered = filtered.filter((rate) => {
          const rateDeadline = rate.cancelPenalties?.[0]?.deadline
          return rateDeadline === this.selectedDeadline
        })
      }

      return filtered
    },
    nonrefundableRates() {
      const code = this.room.lowestNonRefRate?.key
      return this.room.nonRefRates.filter((rate) => code != rate.key)
    },
    selectedRate() {
      return this.room.rates.find((rate) => rate.key === this.selectedRateId)
    },
    policyViolationsCount() {
      return this.selectedRate.travelPolicy?.summary.rulesTriggeredCount || 0
    },
    roomImages() {
      return this.room.roomImages || []
    },
    roomDescription() {
      return this.room.rates[0]?.description || ""
    },
    currencyCode(){
      return this.hotel.currencyCode;
    }
  },
  methods: {
    getAverageBaseNightlyRate(rate) {
      if (rate.requestedCurrencyPricing.baseNightlyRate) {
        return rate.requestedCurrencyPricing.baseNightlyRate
      }
      return rate.requestedCurrencyPricing.dailyRate
    },
    getTotalBaseNightlyRate(rate) {
      return rate.requestedCurrencyPricing.baseNightlyRate ? rate.requestedCurrencyPricing.baseNightlyRate * rate.nights : rate.requestedCurrencyPricing.total
    },
    getTotal(rate) {
      return rate.requestedCurrencyPricing.total
    },
    preloadImages(images) {
      for (let i = 0; i < images.length; ++i) {
        let img = new Image()
        img.src = images[i].url
      }
    },
    prevImage(index) {
      if (index > 0) {
        this.visibleImage = this.room.roomImages[index - 1]
      } else {
        this.visibleImage = this.room.roomImages[
          this.room.roomImages.length - 1
        ]
      }
    },
    nextImage(index) {
      if (index < this.room.roomImages.length - 1) {
        this.visibleImage = this.room.roomImages[index + 1]
      } else {
        this.visibleImage = this.room.roomImages[0]
      }
    },
    getBookingImage() {
      return this.room.roomImages?.length > 0
        ? this.room.roomImages[0].url
        : "https://ganttravel.tripcircuit.com/images/defaultHotel.jpg"
    },
    submitted(selectedRate) {
      if(selectedRate.isMemberRate) {
        this.$buefy.dialog.confirm({
          message: "You’ve selected a member-only rate. Please add the traveler’s membership number on checkout to complete your booking, or switch to a standard rate.",
          confirmText: 'Continue booking',
          type: 'is-info',
          hasIcon: true,
          onConfirm: () => this.processHotelSelection(selectedRate)
        });
      } else {
        this.processHotelSelection(selectedRate)
      }
    },
    processHotelSelection(hotelSegment) {
      const hotel = this.hotel;
      // Log the event
      let eventData = {
        event_type: 'Item Added to Cart',
        event_properties: {
          item_type: 'hotel',
          property_code: hotel.hotelCode,
          is_meeting_mode: this.pathMixin_isMeeting,
        }
      }
      
      this.$app.logEvent(eventData);

      hotelSegment.requestedRateCodes = hotel.requestedRateCodes
      hotelSegment.hotelName = hotel.hotelName
      hotelSegment.chainName = hotel.chainName
      hotelSegment.image = this.getBookingImage()
      hotelSegment.geocode = hotel.geocode
      hotelSegment.sessionKey = this.sessionKey
      this.$store.dispatch("bookTravel/alterHotelResult", hotelSegment)

      // Save hotel search context so the confirmation page can offer a retry
      // if the hotel booking fails (e.g. room no longer available at checkout).
      this.$store.commit("bookTravel/SAVE_HOTEL_SEARCH", {
        hotelCode: hotel.hotelCode,
        hotelName: hotel.hotelName,
        dates: [hotelSegment.startDate, hotelSegment.endDate],
        location: this.$store.state.hotelSearch.searchLocation,
      })

      if (this.pathMixin_isMeeting) {
        this.goToNextStep("cars")
      } else {
        this.$nav.pushRouteTo({ name: "BookingItinerary" })
      }
    },
    onRateClick(rate) {
      this.selectedRateId = rate.key
      this.selectedDeadline = rate.cancelPenalties?.[0]?.deadline || null
    },
    isWebBedsHotel(infoSource) {
      return infoSource == "SJ"
    }
  },
  created() {
    if (this.roomImages.length > 0) {
      this.visibleImage = this.roomImages[0]
      this.preloadImages(this.roomImages)
    } else {
      this.visibleImage = { url: "/images/placeholder.jpg" }
    }
    const initialRate = this.highlightRates[0]
    this.selectedRateId = initialRate.key
    this.selectedDeadline = initialRate.cancelPenalties?.[0]?.deadline || null
  },
  watch: {
    roomImages: {
      handler(newImages) {
        if (newImages.length > 0) {
          this.preloadImages(newImages)
        }
      },
      deep: true,
    },
    selectedRateId(newRateId) {
      // Update selectedDeadline when selectedRateId changes
      if (newRateId) {
        const selectedRate = this.room.rates.find((rate) => rate.key === newRateId)
        if (selectedRate) {
          this.selectedDeadline = selectedRate.cancelPenalties?.[0]?.deadline || null
        }
      }
    },
  },
}
</script>

<style scoped>
.card {
  width: 22rem;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  margin: auto;
}

.card-content {
  padding: 1rem;
}

.image-container {
  position: relative;
  overflow: hidden;
  border-radius: 0.375rem;
}

.image-wrapper {
  position: relative;
  padding-top: 66.67%; /* 3:2 aspect ratio */
}

.room-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.navigation-buttons {
  display: flex;
  justify-content: space-between;
  padding: 1rem;
  margin: auto 0;
}

.nav-button {
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
}

.image-counter {
  background: rgba(0, 0, 0, 0.3);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 9999px;
  font-size: 1rem;
  align-self: center;
  margin-bottom: 1rem;
}

.room-info {
  padding: 0 1rem 0 0;
  border-bottom: 2px solid #e2e8f0;
}

.room-title {
  font-size: 1.25rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
}

.feature-list {
  list-style-type: none;
  font-size: 0.775rem;
  margin-left: 0rem !important;
}

.feature-list li {
  margin-bottom: 0.25rem;
}

.feature-list i {
  color: #48bb78;
  margin-right: 0.5rem;
}

.rate-features {
  list-style-type: none;
  padding-left: 1.5em;
  margin-bottom: 0.5rem;
}

.rate-features li {
  margin-bottom: 0.25rem;
}

.rate-features i {
  color: #48bb78;
  margin-right: 0.5rem;
}

.cancellation-policy {
  padding: 1rem 0;
  border-bottom: 2px solid #e2e8f0;
}

.rate-options {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.rate-option {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  cursor: pointer;
}

.rate-details {
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-left: 0.5rem;
}

.member-rate-tag {
  font-size: 10px;
  font-weight: 500;
  color: #2b6cb0;
  background-color: #bee3f8;
  padding: 2px 7px 2px 10px;
  clip-path: polygon(8px 0%, 100% 0%, 100% 100%, 8px 100%, 0% 50%);
  letter-spacing: 0.04em;
}


.rate-price {
  font-weight: 600;
  min-width: 40px;
  text-align: right;
}

.additional-rates {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 2px solid #e2e8f0;
}

.toggle-rates {
  background: none;
  border: none;
  color: #3182ce;
  font-size: 0.875rem;
  cursor: pointer;
  padding: 0.5rem 0;
  text-decoration: underline;
}

.selected-rate-details {
  padding-top: 1rem;
  font-size: 0.875rem;
}

.price-summary {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 0.5rem;
}

.nightly-rate {
  font-size: 1.875rem;
  font-weight: bold;
  color: #3182ce;
}

.rate-period,
.stay-duration {
  font-size: 0.75rem;
  color: #718096;
  font-weight: normal;
}

.total-price {
  font-size: 1.25rem;
  font-weight: bold;
}

.rate-tags {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.rate-code,
.amenity-tag {
  background-color: #feebc8;
  color: #c05621;
  padding: 0.1rem 0.3rem 0.1rem 0.3rem;
  border-radius: 9999px;
  font-size: 0.7rem;
  font-weight: 700;
}

.non-refundable-warning {
  color: #e53e3e;
  margin-top: 0.5rem;
}

.booking-advice {
  background-color: #e2e8f0;
  color: #4a5568;
  padding: 0.5rem;
  text-align: center;
  margin-top: 1rem;
  font-size: 0.875rem;
}

.book-button {
  width: 100%;
  padding: 0.5rem;
  background-color: #3182ce;
  color: white;
  border: none;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  cursor: pointer;
  margin-top: 1rem;
}

.book-button.disabled {
  background-color: #a0aec0;
  opacity: 0.6;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}

.list-leave-active {
  opacity: 0;
}

.list-move,
.list-enter-active {
  transition: all 0.5s ease;
}

.list-enter-from,
.list-leave-to {
  transform: translateX(100%);
}

.list-leave-active {
  position: absolute;
}
.has-border-bottom-2 {
  border-bottom: 2px solid;
}

.has-border-light {
  border-color: #f5f5f5;
}

.has-border-top-2 {
  border-top: 2px solid;
}
.refundable-title {
  font-weight: 600;
  margin-top: 0.25rem;
  margin-bottom: 0.25rem;
  min-height: 1.5rem;
}

.refundable-title.disabled {
  color: rgb(209 213 219);
}


.price-separator {
  color: #dbdbdb;
  margin: 0 5px;
}
.price-inline-container {
  display: flex;
  align-items: baseline;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0;
  background: #f5f5f5;
  border-radius: 5px;
  padding: 6px 5px;
  border: 1px solid #ddd;
  box-shadow: 0px 2px 0px #ddd;
}
.nightly-price {
  font-size: 1.2rem;
  font-weight: 700;
  color: #363636;
}
.price-label {
  font-size: 0.875rem;
  color: #7a7a7a;
}
.price-value {
  font-weight: 600;
  color: #363636;
}
.total-value {
  
  font-weight: 700;
  color: #3182ce;
}
</style>
